import { authService } from './authService';

// Types for analytics data
export interface ConversationAnalytics {
  id: number;
  session_id: string;
  visitor_id: number;
  lead_id: number | null;
  messages: any[];
  context: any;
  started_at: string;
  ended_at: string | null;
  completed: boolean;
  visitor_uuid: string;
  visitor_email: string | null;
  lead_email: string | null;
  parent_name: string | null;
  child_name: string | null;
  message_count: number;
  user_message_count?: number;
  bot_message_count?: number;
  exchange_count?: number;
  status: 'converted' | 'abandoned' | 'in_progress';
  duration: number | null;
  has_name: boolean;
  has_email: boolean;
  has_child_info: boolean;
  has_phone?: boolean;
  has_phone_consent?: boolean;
}

export interface ConversationFilters {
  status?: 'all' | 'converted' | 'abandoned' | 'in_progress';
  page?: number;
  limit?: number;
  search?: string;
  dateFrom?: string;
  dateTo?: string;
}

export interface ConversationListResponse {
  conversations: ConversationAnalytics[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export interface ConversionFunnelData {
  total_visitors: number;
  total_conversations: number;
  conversations_with_name: number;
  conversations_with_email: number;
  total_leads: number;
  subscribed_leads: number;
  unsubscribed_leads: number;
  bounced_leads: number;
  conversion_rates: {
    visitor_to_conversation: string;
    conversation_to_name: string;
    name_to_email: string;
    email_to_lead: string;
    lead_to_subscribed: string;
    overall_conversion: string;
  };
}

export interface DropOffStage {
  stage: string;
  total: number;
  dropped: number;
  continued: number;
  drop_off_rate: string;
  continuation_rate: string;
}

export interface DropOffAnalysisData {
  stages: DropOffStage[];
  summary: {
    total_conversations: number;
    completed_leads: number;
    overall_completion_rate: string;
  };
}

export interface VisitorJourneyData {
  visitor: {
    id: number;
    uuid: string;
    email: string | null;
    first_seen: string;
    last_seen: string;
    total_visits: number;
  };
  conversations: ConversationAnalytics[];
  lead: any | null;
  summary: {
    total_conversations: number;
    total_messages: number;
    converted_to_lead: boolean;
    first_conversation: string;
    last_conversation: string;
  };
}

export interface ConversationStatsData {
  overview: {
    total_conversations: number;
    converted_conversations: number;
    abandoned_conversations: number;
    active_conversations: number;
    conversion_rate: string;
    avg_duration_minutes: number;
    avg_messages_per_conversation: number;
    unique_visitors: number;
  };
  trends: Array<{
    date: string;
    conversations: number;
    conversions: number;
    conversion_rate: string;
  }>;
  peak_hours: Array<{
    hour: number;
    conversations: number;
    display_hour: string;
  }>;
}

export interface DateFilters {
  dateFrom?: string;
  dateTo?: string;
}

export interface ConversationDetailData {
  conversation: ConversationAnalytics;
  visitor: {
    id: number;
    uuid: string;
    email: string | null;
    first_seen: string;
    last_seen: string;
    total_visits: number;
  };
  lead: {
    id: number;
    email: string;
    parent_name: string;
    child_name: string;
    special_needs: any[];
    timeline: string;
    lead_score: number;
    created_at: string;
  } | null;
  contextEvolution: Array<{
    messageIndex: number;
    timestamp: string;
    extractedData: any;
    completionStatus: {
      hasName: boolean;
      hasEmail: boolean;
      hasChildInfo: boolean;
      hasPhoneConsent: boolean;
    };
  }>;
  conversionPoint: number | null; // Message index where lead was created
}

export interface ConversationSummaryData {
  id: number;
  duration: number | null;
  messageCount: number;
  conversionRate: string;
  keyMoments: Array<{
    type: 'name_provided' | 'email_provided' | 'child_info_provided' | 'phone_consent' | 'lead_created';
    messageIndex: number;
    timestamp: string;
    value?: string;
  }>;
  performance: {
    responseTime: number;
    engagementScore: number;
    completionPercentage: number;
  };
}

const API_BASE_URL = process.env.REACT_APP_API_URL;

class AnalyticsService {
  // Get all conversations with filtering
  async getConversations(filters: ConversationFilters = {}): Promise<ConversationListResponse> {
    const queryParams = new URLSearchParams();
    
    if (filters.status) queryParams.append('status', filters.status);
    if (filters.page) queryParams.append('page', filters.page.toString());
    if (filters.limit) queryParams.append('limit', filters.limit.toString());
    if (filters.search) queryParams.append('search', filters.search);
    if (filters.dateFrom) queryParams.append('dateFrom', filters.dateFrom);
    if (filters.dateTo) queryParams.append('dateTo', filters.dateTo);

    const response = await authService.makeAuthenticatedRequest(
      `${API_BASE_URL}/api/analytics/conversations?${queryParams.toString()}`
    );

    if (!response.ok) {
      throw new Error('Failed to fetch conversations');
    }

    return await response.json();
  }

  // Get conversion funnel metrics
  async getConversionFunnel(filters: DateFilters = {}): Promise<ConversionFunnelData> {
    const queryParams = new URLSearchParams();
    
    if (filters.dateFrom) queryParams.append('dateFrom', filters.dateFrom);
    if (filters.dateTo) queryParams.append('dateTo', filters.dateTo);

    const response = await authService.makeAuthenticatedRequest(
      `${API_BASE_URL}/api/analytics/funnel?${queryParams.toString()}`
    );

    if (!response.ok) {
      throw new Error('Failed to fetch conversion funnel');
    }

    return await response.json();
  }

  // Get drop-off analysis
  async getDropOffAnalysis(filters: DateFilters = {}): Promise<DropOffAnalysisData> {
    const queryParams = new URLSearchParams();
    
    if (filters.dateFrom) queryParams.append('dateFrom', filters.dateFrom);
    if (filters.dateTo) queryParams.append('dateTo', filters.dateTo);

    const response = await authService.makeAuthenticatedRequest(
      `${API_BASE_URL}/api/analytics/drop-off?${queryParams.toString()}`
    );

    if (!response.ok) {
      throw new Error('Failed to fetch drop-off analysis');
    }

    return await response.json();
  }

  // Get visitor journey
  async getVisitorJourney(visitorId: string): Promise<VisitorJourneyData> {
    const response = await authService.makeAuthenticatedRequest(
      `${API_BASE_URL}/api/analytics/visitor-journey/${visitorId}`
    );

    if (!response.ok) {
      throw new Error('Failed to fetch visitor journey');
    }

    return await response.json();
  }

  // Get conversation statistics
  async getConversationStats(filters: DateFilters = {}): Promise<ConversationStatsData> {
    const queryParams = new URLSearchParams();
    
    if (filters.dateFrom) queryParams.append('dateFrom', filters.dateFrom);
    if (filters.dateTo) queryParams.append('dateTo', filters.dateTo);

    const response = await authService.makeAuthenticatedRequest(
      `${API_BASE_URL}/api/analytics/conversation-stats?${queryParams.toString()}`
    );

    if (!response.ok) {
      throw new Error('Failed to fetch conversation statistics');
    }

    return await response.json();
  }

  // Helper method to format date for API calls
  formatDateForAPI(date: Date): string {
    return date.toISOString().split('T')[0];
  }

  // Get detailed conversation information
  async getConversationDetail(conversationId: number): Promise<ConversationDetailData> {
    const response = await authService.makeAuthenticatedRequest(
      `${API_BASE_URL}/api/analytics/conversations/${conversationId}/detail`
    );

    if (!response.ok) {
      throw new Error('Failed to fetch conversation details');
    }

    return await response.json();
  }

  // Get conversation summary metrics
  async getConversationSummary(conversationId: number): Promise<ConversationSummaryData> {
    const response = await authService.makeAuthenticatedRequest(
      `${API_BASE_URL}/api/analytics/conversations/${conversationId}/summary`
    );

    if (!response.ok) {
      throw new Error('Failed to fetch conversation summary');
    }

    return await response.json();
  }

  // Search conversations by message content
  async searchConversationContent(filters: ConversationFilters & { messageSearch?: string }): Promise<ConversationListResponse> {
    const queryParams = new URLSearchParams();

    if (filters.status) queryParams.append('status', filters.status);
    if (filters.page) queryParams.append('page', filters.page.toString());
    if (filters.limit) queryParams.append('limit', filters.limit.toString());
    if (filters.search) queryParams.append('search', filters.search);
    if (filters.messageSearch) queryParams.append('messageSearch', filters.messageSearch);
    if (filters.dateFrom) queryParams.append('dateFrom', filters.dateFrom);
    if (filters.dateTo) queryParams.append('dateTo', filters.dateTo);

    const response = await authService.makeAuthenticatedRequest(
      `${API_BASE_URL}/api/analytics/conversations/search?${queryParams.toString()}`
    );

    if (!response.ok) {
      throw new Error('Failed to search conversations');
    }

    return await response.json();
  }

  // Helper method to get preset date ranges
  getPresetDateRanges() {
    const now = new Date();
    const today = this.formatDateForAPI(now);

    const last7Days = new Date();
    last7Days.setDate(now.getDate() - 7);

    const last30Days = new Date();
    last30Days.setDate(now.getDate() - 30);

    const last90Days = new Date();
    last90Days.setDate(now.getDate() - 90);

    return {
      today: { dateFrom: today, dateTo: today },
      last7Days: { dateFrom: this.formatDateForAPI(last7Days), dateTo: today },
      last30Days: { dateFrom: this.formatDateForAPI(last30Days), dateTo: today },
      last90Days: { dateFrom: this.formatDateForAPI(last90Days), dateTo: today },
      allTime: {}
    };
  }
}

export const analyticsService = new AnalyticsService();
export default analyticsService;